import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Scale, AlertCircle } from 'lucide-react';
import { contentService } from '@/services/api';
import { getMailHref } from '@/lib/contactLinks';

const TermsPage = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'ar').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isRTL = lang === 'ar';

  const [termsContent, setTermsContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lastUpdated = lang === 'ar' ? '1 فبراير 2026' : 'February 1, 2026';

  useEffect(() => {
    const fetchTerms = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First try to fetch from custom_pages endpoint
        const response = await contentService.getCustomPages();
        if (response.data?.data && Array.isArray(response.data.data)) {
          // Find the Terms & Conditions page
          const termsPage = response.data.data.find((page) => {
            const title = (page.title || '').toLowerCase();
            const slug = (page.slug || '').toLowerCase();
            const type = (page.page_type || '').toLowerCase();
            return slug.includes('term') || type === 'terms_of_use' || title.includes('terms');
          });
          
          if (termsPage?.content) {
            setTermsContent(termsPage.content);
            return;
          }
        }
        
        // If custom_pages didn't work, try direct terms endpoint
        const termsResponse = await contentService.getTermsOfUse();
        const htmlContent = typeof termsResponse.data === 'string' 
          ? termsResponse.data 
          : termsResponse.data?.content || termsResponse.data?.data || '';
        
        // Extract just the body content if full HTML is returned
        let bodyContent = htmlContent;
        if (htmlContent.includes('<body>')) {
          const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          if (bodyMatch) {
            bodyContent = bodyMatch[1];
          }
        }
        
        if (bodyContent) {
          setTermsContent(bodyContent);
        } else {
          throw new Error('No content found');
        }
      } catch (err) {
        console.error('Error fetching terms:', err);
        // Use static fallback content instead of showing error
        setTermsContent(getStaticTerms(lang));
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, [lang]);

  // Static fallback content
  const getStaticTerms = (language) => {
    if (language === 'ar') {
      return `
        <h2>شروط الاستخدام</h2>
        <p>مرحبًا بك في دكترها. يرجى قراءة هذه الشروط والأحكام بعناية قبل استخدام المنصة.</p>
        <h3>قبول الشروط</h3>
        <p>باستخدامك لمنصة دكترها، فإنك توافق على الالتزام بهذه الشروط والأحكام.</p>
        <h3>وصف الخدمات</h3>
        <p>توفر دكترها منصة رقمية للبحث عن الأطباء والعيادات وحجز المواعيد الطبية والاستشارات عن بُعد.</p>
        <h3>التزامات المستخدم</h3>
        <p>يجب عليك تقديم معلومات صحيحة ودقيقة، والحفاظ على سرية بيانات حسابك وعدم مشاركتها مع الآخرين.</p>
        <h3>إخلاء المسؤولية</h3>
        <p>دكترها ليست مسؤولة عن جودة الخدمات الطبية المقدمة من الأطباء أو الجهات الصحية، وتقع المسؤولية الطبية على مقدم الخدمة.</p>
      `;
    }
    return `
      <h2>Terms of Service</h2>
      <p>Welcome to Dactrha. Please read these terms and conditions carefully before using our platform.</p>
      <h3>Acceptance of Terms</h3>
      <p>By using the Dactrha platform, you agree to be bound by these terms and conditions.</p>
      <h3>Description of Services</h3>
      <p>Dactrha provides a digital platform for searching doctors and clinics, booking medical appointments, and remote medical consultations.</p>
      <h3>User Rights</h3>
      <p>You must provide accurate and truthful information and keep your account credentials confidential.</p>
      <h3>Disclaimer</h3>
      <p>Dactrha is not responsible for the quality of medical services provided by doctors.</p>
    `;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="terms-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            <Scale className="h-4 w-4 mr-1" />
            {lang === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {lang === 'ar' ? 'شروط الاستخدام' : 'Terms of Service'}
          </h1>
          <p className="text-teal-100">
            {lang === 'ar' ? `آخر تحديث: ${lastUpdated}` : `Last updated: ${lastUpdated}`}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {loading ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-8 w-1/2 mt-6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </CardContent>
          </Card>
        ) : null}

        {/* Terms Content */}
        {!loading && (
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-6 md:p-8">
              <div 
                className="prose prose-slate dark:prose-invert max-w-none
                  prose-headings:text-slate-900 dark:prose-headings:text-white
                  prose-h1:text-2xl prose-h1:font-bold prose-h1:mb-4 prose-h1:text-center
                  prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-teal-700 dark:prose-h2:text-teal-400
                  prose-h3:text-lg prose-h3:font-medium prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                  prose-li:text-slate-600 dark:prose-li:text-slate-300 prose-li:mb-2
                  prose-strong:text-slate-900 dark:prose-strong:text-white prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: termsContent }}
              />
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm mt-8 bg-teal-50 dark:bg-teal-900/20">
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 text-teal-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {lang === 'ar' ? 'هل لديك أسئلة؟' : 'Have Questions?'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {lang === 'ar' 
                ? 'إذا كانت لديك أي أسئلة حول الشروط والأحكام، يرجى التواصل معنا.'
                : 'If you have any questions about our terms and conditions, please contact us'}
            </p>
            <a
              href={getMailHref('legal@dactrha.com')}
              className="text-teal-600 font-medium transition-colors hover:text-teal-700 hover:underline dark:text-teal-400"
            >
              <span dir="ltr">legal@dactrha.com</span>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;

